'use client';

import { useState, type Dispatch } from 'react';
import type { GeofenceOverlay } from './DroneMap';
import { X, Circle, Hexagon } from 'lucide-react';
import { ZonesList } from './zones/ZonesList';
import { ZoneDrawForm } from './zones/ZoneDrawForm';
import type { ZoneForm, ZoneFormAction } from './zones/zoneForm';
import { resolveZoneColor } from './zones/zoneColors';

function buildCircleRing(lat: number, lng: number, radiusM: number, steps = 32): [number, number][] {
  const coords: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    coords.push([
      lng + (radiusM / 111320) * Math.cos(angle) / Math.cos((lat * Math.PI) / 180),
      lat + (radiusM / 110540) * Math.sin(angle),
    ]);
  }
  return coords;
}

export interface SaveGeofenceData {
  name: string;
  type: string;
  color: string;
  max_altitude?: number;
  alert_enabled: boolean;
  geometry: { type: 'Polygon'; coordinates: number[][][] };
}

export interface ZonesPanelProps {
  geofences: GeofenceOverlay[];
  onSaveGeofence: (data: SaveGeofenceData) => Promise<void>;
  onDeleteGeofence: (id: string) => Promise<void>;
  onZoomToGeofence: (geometry: GeoJSON.Geometry) => void;
  onClose: () => void;
  drawMode: 'circle' | 'polygon' | null;
  onDrawModeChange: (mode: 'circle' | 'polygon' | null) => void;
  drawPoints: [number, number][];
  polygonClosed: boolean;
  onClosePolygon: () => void;
  circleCenter: { lat: number; lng: number } | null;
  onSetCircleCenter: (lat: number, lng: number) => void;
  circleRadius: number;
  onCircleRadiusChange: (r: number) => void;
  /**
   * The draw form lives with the caller, not here, because the map preview has
   * to be painted in the colour this form holds — and the map is the panel's
   * sibling, not its child. Every other piece of draw state (points, circle
   * centre, radius) is already owned up there for the same reason.
   */
  form: ZoneForm;
  dispatchForm: Dispatch<ZoneFormAction>;
}

export function ZonesPanel({
  geofences,
  onSaveGeofence,
  onDeleteGeofence,
  onZoomToGeofence,
  onClose,
  drawMode,
  onDrawModeChange,
  drawPoints,
  polygonClosed,
  onClosePolygon,
  circleCenter,
  onSetCircleCenter,
  circleRadius,
  onCircleRadiusChange,
  form,
  dispatchForm,
}: ZonesPanelProps) {
  const [saving, setSaving] = useState(false);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const startDraw = (mode: 'circle' | 'polygon') => {
    dispatchForm({ kind: 'reset' });
    onDrawModeChange(mode);
  };

  const cancelDraw = () => {
    onDrawModeChange(null);
    dispatchForm({ kind: 'reset' });
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;

    let geometry: { type: 'Polygon'; coordinates: number[][][] } | null = null;

    if (drawMode === 'circle') {
      if (!circleCenter || circleRadius <= 0) return;
      geometry = {
        type: 'Polygon',
        coordinates: [buildCircleRing(circleCenter.lat, circleCenter.lng, circleRadius)],
      };
    } else if (drawMode === 'polygon') {
      if (drawPoints.length < 3 || !polygonClosed) return;
      geometry = {
        type: 'Polygon',
        coordinates: [[...drawPoints, drawPoints[0]]],
      };
    }

    if (!geometry) return;

    setSaving(true);
    try {
      await onSaveGeofence({
        name: form.name.trim(),
        type: form.type,
        color: resolveZoneColor(form.type, form.color),
        max_altitude: form.maxAlt ? parseFloat(form.maxAlt) : undefined,
        alert_enabled: form.alertEnabled,
        geometry,
      });
      cancelDraw();
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await onDeleteGeofence(id);
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const canSave =
    form.name.trim().length > 0 &&
    (drawMode === 'circle'
      ? !!circleCenter && circleRadius > 0
      : drawMode === 'polygon'
        ? drawPoints.length >= 3 && polygonClosed
        : false);

  return (
    <div
      // At `lg` this is a SHEET in the right column (slice 8), so it takes the
      // column's width and drops its own ground, border and corners: the host
      // paints those once for every sheet, and a second rounded card inside a
      // square column reads as a panel that failed to fit. Below `lg` the class
      // list is unchanged - the floating 300 px card is what the drawer layout
      // still expects, and nothing below `lg` moves in this plan.
      className="w-[300px] shrink-0 flex flex-col rounded-md bg-background/[0.92] backdrop-blur-sm border border-subtle overflow-hidden lg:w-full lg:min-h-0 lg:flex-1 lg:rounded-none lg:border-0 lg:bg-transparent lg:backdrop-blur-none"
      style={{ maxHeight: '100%' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b divider shrink-0">
        <div className="flex items-center gap-2">
          <Hexagon className="size-3.5 text-muted-foreground" strokeWidth={1.5} />
          <span className="text-[10px] text-muted-foreground uppercase tracking-wider">
            ZONES
          </span>
          <span className="font-mono text-xs text-primary font-bold tabular-nums">
            {geofences.length}
          </span>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-black/[0.06] dark:hover:bg-white/10 text-muted-foreground"
          aria-label="Close zones panel"
        >
          <X className="size-4" strokeWidth={1.5} />
        </button>
      </div>

      {/* Geofence list */}
      <ZonesList
        geofences={geofences}
        hasDrawMode={drawMode !== null}
        deleteConfirmId={deleteConfirmId}
        onZoomToGeofence={onZoomToGeofence}
        onRequestDelete={setDeleteConfirmId}
        onConfirmDelete={handleDelete}
      />

      {/* Draw mode form */}
      {drawMode && (
        <ZoneDrawForm
          drawMode={drawMode}
          drawPoints={drawPoints}
          polygonClosed={polygonClosed}
          onClosePolygon={onClosePolygon}
          circleCenter={circleCenter}
          onSetCircleCenter={onSetCircleCenter}
          circleRadius={circleRadius}
          onCircleRadiusChange={onCircleRadiusChange}
          form={form}
          dispatchForm={dispatchForm}
          saving={saving}
          canSave={canSave}
          onCancel={cancelDraw}
          onSave={handleSave}
        />
      )}

      {/* Add zone buttons */}
      {!drawMode && (
        <div className="border-t divider p-2 flex gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => startDraw('circle')}
            className="flex-1 flex items-center justify-center gap-1.5 h-7 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground border border-subtle rounded hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors"
          >
            <Circle className="size-3" strokeWidth={1.5} />
            Circle
          </button>
          <button
            type="button"
            onClick={() => startDraw('polygon')}
            className="flex-1 flex items-center justify-center gap-1.5 h-7 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground border border-subtle rounded hover:bg-black/[0.03] dark:hover:bg-white/5 transition-colors"
          >
            <Hexagon className="size-3" strokeWidth={1.5} />
            Polygon
          </button>
        </div>
      )}
    </div>
  );
}
